import { Button, Tabs } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./SuperAdminSidebar.css";
import LinkTab from "./LinkTab";
import styled from "@emotion/styled";
import user from "../../../assets/images/sidebar-icons/user.png";
import userFill from "../../../assets/images/sidebar-icons/user-fill.png";
import client from "../../../assets/images/sidebar-icons/client.png";
import clientFill from "../../../assets/images/sidebar-icons/client-fill.png";
import plan from "../../../assets/images/sidebar-icons/plan.png";
import planFill from "../../../assets/images/sidebar-icons/plan-fill.png";
import dashbaord from "../../../assets/images/sidebar-icons/dashboard.svg";
import dashbaordFill from "../../../assets/images/sidebar-icons/dashboardFill.svg";
import setting from "../../../assets/images/sidebar-icons/setting.png";
import settingFill from "../../../assets/images/sidebar-icons/setting-fill.png";
import pro from "../../../assets/images/sidebar-icons/pro.svg";
import proFill from "../../../assets/images/sidebar-icons/proFill.svg";
import complaints from "../../../assets/complaints.svg";
import complaintsFill from "../../../assets/complaintFill.svg";
import { useSelector } from "react-redux";
import {
  AssignmentIndOutlined,
  DirectionsCarFilled,
  DirectionsCarFilledOutlined,
  Groups2Outlined,
  WarehouseOutlined,
  Warehouse,
} from "@mui/icons-material";

interface TabStructure {
  id: number;
  label: string;
  link: string;
  icon: JSX.Element;
  iconActive: JSX.Element;
  children?: TabStructure[]; // 👈 allow sub-tabs
}

// Styled Tabs with custom appearance
const StyledTabs = styled(Tabs)<{ indicatorVisible: boolean }>(
  ({ indicatorVisible }) => ({
    "& .MuiTabs-root": {
      minHeight: "65px",
      fontSize: "1rem",
      color: "#333",
      textTransform: "none",
      boxShadow: "2px 2px 5px rgba(0,0,0,0.2)",
      borderRadius: "0 25px 25px 0",
      justifyContent: "flex-start",
      padding: "4px 10px",
      position: "relative",
      borderBottom: "1px solid #C4CDD5",
    },
    "& .MuiTabs-scroller": {
      // maxHeight: 290,
      minWidth: 100,
      width: 100,
      margin: "4px 0",
    },
    "& .MuiButtonBase-root": {
      fontSize: "12px",
      textTransform: "capitalize",
      color: "e4e4e4",
      fontWeight: 700,
      borderBottom: "1px solid #C4CDD5",
    },
    "& .Mui-selected": {
      backgroundColor: "#e0f7fa",
      color: "#00796b",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "transparent",
      width: 0,
      bottom: 0,
      height: "56px !important",
      transform: "translateY(0px)",
      right: 0,
      borderLeft: "16px solid transparent",
      borderRight: "0px solid transparent",
      borderBottom: `16px solid ${
        indicatorVisible ? "#086d6a" : "transparent"
      }`,
    },
  })
);

const SuperAdminSidebar: React.FC = () => {
  const [value, setValue] = useState<number | null>(null);
  const [hoveredTab, setHoveredTab] = useState<number | null>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null); // ✅ new state

  const navigate = useNavigate();
  const location = useLocation();

  const { userProfile } = useSelector((state: any) => state?.user);

  const [isBasicDiarySection, setIsBasicDiarySection] = useState(
    userProfile?.features?.includes("Basic Work Diary") || false // Safe fallback
  );
  const [isOperationsSection, setIsOperationsSection] = useState(
    userProfile?.features?.includes("Operations") || false // Safe fallback
  );

  useEffect(() => {
    setIsBasicDiarySection(
      userProfile?.features?.includes("Basic Work Diary") || false
    ); // Sync profile changes
    setIsOperationsSection(
      userProfile?.features?.includes("Operations") || false
    ); // Sync profile changes
  }, [userProfile]);

  const initialAdminStructure: TabStructure[] = useMemo(() => {
    const tabs: TabStructure[] = [];

    if (!isBasicDiarySection) {
      tabs.push({
        id: 0,
        label: "Dashboard",
        link: "/dashboard-report",
        icon: (
          <img
            src={dashbaord}
            alt="user"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        iconActive: (
          <img
            src={dashbaordFill}
            alt="user"
            style={{ height: "24px", width: "24px" }}
          />
        ),
      });
    }

    tabs.push(
      {
        id: 1,
        label: "Removals",
        link: "/dashboard",
        icon: (
          <img
            src={user}
            alt="user"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        iconActive: (
          <img
            src={userFill}
            alt="user"
            style={{ height: "24px", width: "24px" }}
          />
        ),
      },
      // {
      //   id: 10,
      //   label: "Storage",
      //   link: "/storage",
      //   icon: <WarehouseOutlined sx={{ fontSize: 24, color: "#666" }} />,
      //   iconActive: <Warehouse sx={{ fontSize: 24, color: "#00796b" }} />,
      // },
      {
        id: 2,
        label: "Diary",
        link: "/diary",
        icon: (
          <img
            src={client}
            alt="client"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        iconActive: (
          <img
            src={clientFill}
            alt="client"
            style={{ height: "24px", width: "24px" }}
          />
        ),
      },
      {
        id: 3,
        label: "Accounts",
        link: "/account-settings",
        icon: (
          <img
            src={plan}
            alt="invoice"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        iconActive: (
          <img
            src={planFill}
            alt="invoice"
            style={{ height: "24px", width: "24px" }}
          />
        ),
      },
      {
        id: 4,
        label: "Settings",
        link: "/settings",
        icon: (
          <img
            src={setting}
            alt="support"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        iconActive: (
          <img
            src={settingFill}
            alt="support"
            style={{ height: "24px", width: "24px" }}
          />
        ),
      }
    );
    // Professional plan
    if (isOperationsSection) {
      tabs.push({
        id: 5,
        label: "Operations",
        link: "#",
        icon: (
          <img
            src={pro}
            alt="Operations"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        iconActive: (
          <img
            src={proFill}
            alt="Operations"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        children: [
          {
            id: 7,
            label: "Vehicles",
            link: "/operation/vehicles",
            icon: <DirectionsCarFilledOutlined fontSize="small" />,
            iconActive: (
              <DirectionsCarFilled fontSize="small" sx={{ color: "teal" }} />
            ),
          },
          {
            id: 7,
            label: "Staff",
            link: "/operation/staff",
            icon: <Groups2Outlined fontSize="small" />,
            iconActive: (
              <Groups2Outlined fontSize="small" sx={{ color: "teal" }} />
            ),
          },
          {
            id: 6,
            label: "Worker Assign",
            link: "/operation/assign-worker",
            icon: <AssignmentIndOutlined fontSize="small" />,
            iconActive: (
              <AssignmentIndOutlined fontSize="small" sx={{ color: "teal" }} />
            ),
          },
          {
            id: 8,
            label: "Subcontractors",
            link: "/operation/subcontractors",
            icon: <DirectionsCarFilledOutlined fontSize="small" />,
            iconActive: (
              <DirectionsCarFilled fontSize="small" sx={{ color: "teal" }} />
            ),
          },
           {
            id: 9,
            label: "Task Sign Off",
            link: "/operation/task-signoff",
            icon: <DirectionsCarFilledOutlined fontSize="small" />,
            iconActive: (
              <DirectionsCarFilled fontSize="small" sx={{ color: "teal" }} />
            ),
          }
        ],  
      });
      // add complaints menu here when needed
      tabs.push({
        id: 6,
        label: "Complaints",
        link: "/complaints",
        icon: (
          <img
            src={complaints}
            alt="complaints"
            style={{ height: "24px", width: "24px" }}
          />
        ),
        iconActive: (
          <img
            src={complaintsFill}
            alt="complaints"
            style={{ height: "24px", width: "24px" }}
          />
        ),
      });

      // add job completion menu here when needed
      // tabs.push({
      //   id: 6,
      //   label: "Job Completion",
      //   link: "/job-completion",
      //   icon: (
      //     <img
      //       src={complaints}
      //       alt="completion"
      //       style={{ height: "24px", width: "24px" }}
      //     />
      //   ),
      //   iconActive: (
      //     <img
      //       src={complaintsFill}
      //       alt="completion"
      //       style={{ height: "24px", width: "24px" }}
      //     />
      //   ),
      // });
    }

    return tabs;
  }, [isBasicDiarySection, isOperationsSection]);

  useEffect(() => {
    const currentPath = location.pathname;
    let currentTab = initialAdminStructure.findIndex(
      (tab) => tab.link === currentPath
    );
    // ✅ If not direct match, check children
    if (currentTab === -1) {
      currentTab = initialAdminStructure.findIndex((tab) =>
        tab.children?.some((child) => currentPath.startsWith(child.link))
      );
    }
    if (currentTab !== -1) {
      setValue(currentTab);
      localStorage.setItem("activeTab", currentTab.toString()); // Keep in sync
    } else {
      setValue(null);
    }
  }, [location.pathname, initialAdminStructure]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
      localStorage.setItem("activeTab", newValue.toString());
      navigate(initialAdminStructure[newValue].link);
    },
    [navigate, initialAdminStructure]
  );

  useEffect(() => {
    const savedValue = localStorage.getItem("activeTab");
    if (savedValue && location.pathname !== "/") {
      setValue(Number(savedValue));
    }
  }, [location.pathname]);

  return (
    <div
      className={`main-sidebar`}
      style={{
        filter: "drop-shadow(5px 5px 10px #f0f0f0)",
        background: "#fff",
        height: "-webkit-fill-available ",
      }}
    >
      <StyledTabs
        orientation="vertical"
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        indicatorVisible={value !== null}
      >
        {initialAdminStructure.map((tab, index) => {
          const isHovered = hoveredTab === index;
          const isActive = value === index;
          const showSubmenu = openMenuIndex === index || isHovered;

          return (
            <div
              key={tab.id}
              style={{ position: "relative" }}
              onMouseEnter={() => {
                if (openMenuIndex === null) setHoveredTab(index); // ✅ show on hover if not manually opened
              }}
              onMouseLeave={() => {
                if (openMenuIndex === null) setHoveredTab(null); // ✅ hide only if not manually toggled
              }}
            >
              {/* === Parent Tab === */}
              <LinkTab
                key={tab.id}
                icon={isActive ? tab.iconActive : tab.icon}
                label={tab.label}
                href={tab.link}
                value={index}
                setValue={setValue}
                isSelected={isActive}
                onClick={() => {
                  // ✅ toggle submenu manually on click
                  if (openMenuIndex === index) {
                    setOpenMenuIndex(null); // close if already open
                    setHoveredTab(null);
                  } else if (tab.children) {
                    setOpenMenuIndex(index); // open submenu
                    setHoveredTab(index);
                  } else {
                    setOpenMenuIndex(null); // no submenu
                    setHoveredTab(null);
                  }
                }}
              />

              {/* === Submenu === */}
              {tab.children && tab.children.length > 0 && showSubmenu && (
                <div
                  onMouseEnter={() => setHoveredTab(index)} // keep open while hovering submenu
                  onMouseLeave={() => {
                    if (openMenuIndex === null) setHoveredTab(null);
                  }}
                  style={{
                    position: "fixed",
                    top: `calc(70px + ${index * 48}px)`,
                    left: "100%",
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 9999999,
                    minWidth: "130px",
                    padding: "8px 0",
                    display: "grid",
                    pointerEvents: "auto",
                    textAlign: "center",
                    transition: "opacity 0.2s ease-in-out",
                    opacity: showSubmenu ? 1 : 0,
                  }}
                >
                  {tab.children.map((child) => {
                    const childActive = location.pathname.startsWith(
                      child.link
                    );
                    return (
                      <LinkTab
                        key={child.id}
                        icon={childActive ? child.iconActive : child.icon}
                        label={child.label}
                        href={child.link}
                        value={child.id}
                        setValue={setValue}
                        isSelected={childActive}
                        onClick={() => {
                          setHoveredTab(null);
                          setOpenMenuIndex(null); // ✅ hide submenu after clicking child
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </StyledTabs>
    </div>
  );
};

export default SuperAdminSidebar;
